import { ChartConfiguration, TooltipItem } from 'chart.js';

/**
 * Chart.js styling for the report graphs, read from the console's bk-* tokens
 * so the charts follow light and dark mode: ink bars on the track colour,
 * Comfortaa labels, hairline grid, no legend boxes or gradients.
 */
export interface ChartTokens {
  font: string;
  ink: string;
  ash: string;
  faint: string;
  rule: string;
  track: string;
  panel: string;
  invert: string;
  onInvert: string;
  bad: string;
}

const LIGHT: ChartTokens = {
  font: '"Comfortaa", sans-serif',
  ink: '#0a0a0a',
  ash: '#6b6b6b',
  faint: '#9b9b9b',
  rule: '#eeeeee',
  track: '#e6e6e6',
  panel: '#ffffff',
  invert: '#0a0a0a',
  onInvert: '#ffffff',
  bad: '#dc2626',
};

/** Token values as computed for an element (so a `.dark` ancestor applies). */
export function readChartTokens(el: HTMLElement | null | undefined): ChartTokens {
  if (!el || typeof window === 'undefined' || typeof getComputedStyle !== 'function') return LIGHT;
  const style = getComputedStyle(el);
  const read = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
  return {
    font: read('--bk-font', LIGHT.font),
    ink: read('--bk-ink', LIGHT.ink),
    ash: read('--bk-ash', LIGHT.ash),
    faint: read('--bk-faint', LIGHT.faint),
    rule: read('--bk-rule', LIGHT.rule),
    track: read('--bk-track', LIGHT.track),
    panel: read('--bk-panel', LIGHT.panel),
    invert: read('--bk-invert', LIGHT.invert),
    onInvert: read('--bk-on-invert', LIGHT.onInvert),
    bad: read('--bk-bad', LIGHT.bad),
  };
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/** Bar height plus gap per row, and room for the value axis. */
export const BAR_ROW_PX = 34;
const AXIS_PX = 34;

/** Chart height for a horizontal bar chart with this many rows. */
export function barChartHeight(rows: number): number {
  return Math.max(2, rows) * BAR_ROW_PX + AXIS_PX;
}

function shorten(label: unknown, max = 26): string {
  const text = String(label ?? '');
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * Horizontal 0–100% bars: the value in ink, the rest of the row in the track
 * colour, labels on the left. `describe` writes the tooltip line.
 */
export function shareBarConfig(
  tokens: ChartTokens,
  labels: string[],
  values: number[],
  describe: (index: number, value: number) => string
): { data: ChartConfiguration<'bar'>['data']; options: ChartConfiguration<'bar'>['options'] } {
  const clamped = values.map((value) => Math.max(0, Math.min(100, Number(value) || 0)));

  const data: ChartConfiguration<'bar'>['data'] = {
    labels,
    // Two overlapping bars per row, not a stack: the full-width track behind,
    // the share in ink in front, both with round ends.
    datasets: [
      {
        label: 'Share',
        data: clamped,
        backgroundColor: tokens.ink,
        hoverBackgroundColor: tokens.ink,
        borderRadius: 7,
        borderSkipped: false,
        barThickness: 14,
        grouped: false,
        order: 1,
      },
      {
        label: 'Track',
        data: clamped.map(() => 100),
        backgroundColor: tokens.track,
        hoverBackgroundColor: tokens.track,
        borderRadius: 7,
        borderSkipped: false,
        barThickness: 14,
        grouped: false,
        order: 2,
      },
    ],
  };

  const options: ChartConfiguration<'bar'>['options'] = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    animation: prefersReducedMotion() ? false : { duration: 240, easing: 'easeOutQuart' },
    layout: { padding: { right: 4 } },
    // Anywhere along a row (on the ink or the track) shows that row's figure.
    interaction: { mode: 'index', axis: 'y', intersect: false },
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        enabled: true,
        filter: (item: TooltipItem<'bar'>) => item.datasetIndex === 0,
        backgroundColor: tokens.invert,
        titleColor: tokens.onInvert,
        bodyColor: tokens.onInvert,
        titleFont: { family: tokens.font, size: 12, weight: 'bold' },
        bodyFont: { family: tokens.font, size: 12 },
        padding: 10,
        cornerRadius: 10,
        displayColors: false,
        callbacks: {
          label: (item: TooltipItem<'bar'>) => describe(item.dataIndex, Number(item.raw) || 0),
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        border: { display: false },
        grid: { display: false },
        ticks: {
          stepSize: 25,
          padding: 8,
          color: tokens.ash,
          font: { family: tokens.font, size: 11 },
          callback: (value) => `${value}%`,
        },
      },
      y: {
        border: { display: false },
        grid: { display: false },
        ticks: {
          padding: 8,
          color: tokens.ink,
          font: { family: tokens.font, size: 12, weight: 'bold' },
          callback: function (value) {
            // `this` is the scale: map the index back to its label.
            return shorten((this as any).getLabelForValue(Number(value)));
          },
        },
      },
    },
  };

  return { data, options };
}

/** "87%", "99.4%": a decimal only where it changes the meaning. */
export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value > 99 && value < 100) return `${Math.floor(value * 10) / 10}%`;
  return `${Math.round(value)}%`;
}
